import React, { useState } from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Panel, Segments, Empty, Modal, useFlash, useModal } from '../../ui/kit.js';
import { Note, useLast } from '../../ui/actions/parts.js';
import { day, RESIDENT } from '../../state/building.js';

import Documents from '../../media/images/modules/documents.jpg';

const CATEGORIES = ['Governing documents', 'Meeting minutes', 'Budgets', 'Notices', 'Your unit'];

const DOCUMENTS = [
    { id: 1, category: 'Governing documents', title: 'Declaration of Condominium', date: 'Mar 14, 2019', size: '4.2 MB', excerpt: 'The Association shall be responsible for the maintenance, repair and replacement of all common elements, including the seawall, pool deck and parking structure.' },
    { id: 2, category: 'Governing documents', title: 'Bylaws, as amended 2024', date: 'Feb 2, 2024', size: '1.1 MB', excerpt: 'The Board shall consist of five directors elected by the unit owners to staggered two-year terms.' },
    { id: 3, category: 'Governing documents', title: 'Rules and regulations', date: 'Jan 8, 2026', size: '860 KB', excerpt: 'Balcony grills are limited to electric models. Pool hours are 7:00 AM to 10:00 PM daily.' },
    { id: 4, category: 'Meeting minutes', title: 'Board meeting minutes, September', date: day(-12), size: '310 KB', excerpt: 'Motion to approve the seawall repair contract with Biscayne Marine passed 4 to 1.' },
    { id: 5, category: 'Meeting minutes', title: 'Annual owners meeting minutes', date: 'Jan 21, 2026', size: '540 KB', excerpt: 'Two directors elected. Owners were briefed on the milestone inspection timeline.' },
    { id: 6, category: 'Budgets', title: '2026 operating budget', date: 'Nov 18, 2025', size: '720 KB', excerpt: 'Total operating expenses of $3.84M, with insurance representing 31% of the budget.' },
    { id: 7, category: 'Budgets', title: 'Structural integrity reserve study', date: 'Aug 30, 2025', size: '6.8 MB', excerpt: 'Fully funded reserves recommended for roof, structure, fireproofing and waterproofing over 30 years.' },
    { id: 8, category: 'Notices', title: 'Hurricane season preparedness', date: day(-40), size: '220 KB', excerpt: 'Remove all balcony furniture when a hurricane warning is issued. The garage will close two hours before landfall.' },
    { id: 9, category: 'Notices', title: 'Pool deck resurfacing schedule', date: day(-5), size: '180 KB', excerpt: 'The south pool deck will close for resurfacing for ten days. The spa remains open.' },
    { id: 10, category: 'Your unit', title: 'Unit 1204 warranty deed', date: 'Jun 3, 2021', size: '2.4 MB', excerpt: 'Grantor conveys to Alex Rivera all that certain condominium parcel known as Unit 1204.' },
    { id: 11, category: 'Your unit', title: 'Estoppel certificate', date: day(-60), size: '140 KB', excerpt: 'Assessments for Unit 1204 are paid through the current quarter. No violations are pending.' }
];

/* A plausible page count from the file size, for the viewer's footer. */
const pagesOf = doc => Math.max(1, Math.round(parseFloat(doc.size) * (doc.size.includes('MB') ? 14 : 0.014)));

/* Documents / Association Records: search, browse by category, open.
 *
 * Opening a document used to fill a preview panel beside the list. On a
 * phone that panel sat below the whole list, so tapping View appeared
 * to do nothing; the document now opens in a sheet at every size, and
 * the panel keeps the ones opened recently. */
function DocumentsScreen() {
    const [flash, notify] = useFlash();
    const [category, setCategory] = useState('Governing documents');
    const [query, setQuery] = useState('');
    const [recent, setRecent] = useState([]);
    const viewer = useModal();

    const q = query.trim().toLowerCase();
    const shown = DOCUMENTS.filter(d => (q ? d.title.toLowerCase().includes(q) || d.excerpt.toLowerCase().includes(q) : d.category === category));

    const view = doc => {
        viewer.open(doc);
        setRecent(list => [doc.id, ...list.filter(id => id !== doc.id)].slice(0, 5));
    };

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Association records" title="Documents" />
            {flash}

            <div className="banner">
                <img src={Documents} alt="" />
                <p className="banner__caption">Governing documents, minutes and budgets, all in one place.</p>
            </div>

            <div className="stack">
                <Segments label="Category" options={CATEGORIES} value={category} onChange={c => { setCategory(c); setQuery(''); }} />
                <input type="search" className="form-control" aria-label="Search all documents" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search all documents, e.g. pool, budget, hurricane" />

                <div className="split">
                    <div className="stack">
                        <h4>{q ? `Results for "${query.trim()}"` : category}</h4>
                        {shown.length === 0 ? <Empty>No documents match that search.</Empty> : (
                            <ul className="rows">
                                {shown.map(d => (
                                    <li className="row" key={d.id}>
                                        <div className="row__main">
                                            <span className="row__title">{d.title}</span>
                                            <span className="row__meta">{q ? `${d.category} · ` : ''}{d.date} · PDF · {d.size}</span>
                                        </div>
                                        <div className="row__end">
                                            <button type="button" className="btn btn-outline-light btn-sm" onClick={() => view(d)}>View</button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <Panel className="panel--accent">
                        <h4>Recently opened</h4>
                        {recent.length === 0 ? (
                            <p className="row__meta">Documents you open appear here, so the one you were reading is a tap away.</p>
                        ) : (
                            <ul className="rows">
                                {recent.map(id => {
                                    const d = DOCUMENTS.find(doc => doc.id === id);
                                    return (
                                        <li className="row" key={id}>
                                            <div className="row__main">
                                                <span className="row__title">{d.title}</span>
                                                <span className="row__meta">{d.category} · {d.size}</span>
                                            </div>
                                            <div className="row__end">
                                                <button type="button" className="btn btn-outline-light btn-sm" onClick={() => view(d)}>Open</button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </Panel>
                </div>
            </div>

            <DocumentSheet item={viewer.item} onClose={viewer.close} onSent={notify} />
        </div>
    );
}

/* The document itself: a page of it, and the two ways to take it away
   from a lobby screen, by email or printed at the desk. */
function DocumentSheet({ item, onClose, onSent }) {
    const shown = useLast(item);
    const [note, setNote] = useState(null);
    const [openFor, setOpenFor] = useState(null);

    if (item !== openFor) {
        setOpenFor(item);
        if (item) setNote(null);
    }

    if (!shown) return <Modal open={false} onClose={onClose} />;
    const doc = shown;
    const pages = pagesOf(doc);

    const send = how => {
        const text = how === 'email'
            ? `Emailed to the address on file for unit ${RESIDENT.unit}, with the ${pages}-page PDF attached.`
            : `Sent to the front desk printer. Your copy will be ready in about five minutes.`;
        setNote({ text, key: Date.now() });
        onSent(text);
    };

    return (
        <Modal
            open={Boolean(item)}
            onClose={onClose}
            size="lg"
            eyebrow={doc.category}
            title={doc.title}
            actions={
                <>
                    <button type="button" className="btn btn-outline-light" onClick={() => send('print')}>Print at the front desk</button>
                    <button type="button" className="btn btn-primary" onClick={() => send('email')}>Email me a copy</button>
                </>
            }
        >
            <div className="row__end" style={{ justifyContent: 'flex-start' }}>
                <Chip tone="info">PDF · {doc.size}</Chip>
                <Chip tone="muted">{pages} {pages === 1 ? 'page' : 'pages'}</Chip>
                <Chip tone="muted">Posted {doc.date}</Chip>
            </div>

            <article className="doc-page" aria-label={`${doc.title}, page 1 of ${pages}`}>
                <span className="doc-page__org">Condominium Association, Inc.</span>
                <h4 className="doc-page__title">{doc.title}</h4>
                <span className="doc-page__meta">{doc.category} · {doc.date}</span>
                <p className="doc-page__text">{doc.excerpt}</p>
                {/* The rest of the page, set as type would be, without
                    pretending to be text anyone should read. */}
                <span className="doc-page__lines" aria-hidden="true">
                    {[92, 100, 96, 88, 100, 74, 0, 98, 94, 100, 61].map((w, i) => (
                        <span key={i} style={{ width: `${w}%` }} />
                    ))}
                </span>
                <span className="doc-page__foot">Page 1 of {pages}</span>
            </article>

            <Note note={note} />
        </Modal>
    );
}

export default DocumentsScreen;
